/**
 * #832 (office audit 4, C4/M1) — wait-age, in one place.
 *
 * The close-out queue could rot silently: cards showed the SERVICE date, not
 * how long the job has been waiting on the office, and a 23-day-old "Missing
 * Photos" flag looked identical to a 2-hour-old one. Office Review next door
 * already rendered "22d ago" on every card — this module is that treatment
 * extracted so every queue speaks the same age language and shares one
 * escalation threshold.
 *
 * Pure functions only (no imports, no Date.now() at module scope) so the
 * tests can pin exact days.
 */

/** Days past which a waiting item is considered stalling and gets the
 *  escalation tint (issue #832 suggests 7). One constant, every queue. */
export const STALE_AFTER_DAYS = 7;

/** Whole days between `iso` and `now`, clamped at 0 (future/clock-skew reads
 *  as today, never "-1d"). Null when the timestamp is absent or unparsable. */
export function waitDays(iso: string | null | undefined, now: Date): number | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
}

/** "today" / "1d waiting" / "23d waiting" — the queue-card age chip text.
 *  Null in, null out (callers render nothing rather than a lie). */
export function waitAgeLabel(days: number | null): string | null {
    if (days === null) return null;
    if (days <= 0) return "today";
    return `${days}d waiting`;
}

/** Compact "22d ago"-style label for flag pills and secondary chrome. */
export function agoLabel(days: number | null): string | null {
    if (days === null) return null;
    if (days <= 0) return "today";
    return `${days}d ago`;
}

export function isStale(days: number | null, threshold: number = STALE_AFTER_DAYS): boolean {
    return days !== null && days >= threshold;
}
